import { useCallback } from "react";
import { Feature, MultiPolygon, Polygon } from "geojson";

import { useUpdateVersionMutation } from "fond/api";
import { polygonSelect, setActiveLayerGroupId } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BOUNDARY_POLYGON_SELECT_LAYER_IDS, toMultiPolygonFeature } from "./boundary";

/** Return a callback that begins polygon select for the boundary, with addresses, parcels and streets selected for import. */
export const useBeginBoundarySelect = (): (() => void) => {
  const dispatch = useAppDispatch();

  return useCallback(() => {
    // Polygon select imports addresses, parcels and streets when no layer group is active.
    dispatch(setActiveLayerGroupId(null));
    dispatch(polygonSelect({ layerIds: BOUNDARY_POLYGON_SELECT_LAYER_IDS }));
  }, [dispatch]);
};

/** Return a callback that sets the version's boundary (`version.UploadedArea`) without importing any data. */
export const useSaveBoundary = (): { saveBoundary(area: Feature<Polygon | MultiPolygon>): Promise<void>; isSaving: boolean } => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [updateVersion, { isLoading }] = useUpdateVersionMutation();

  const saveBoundary = useCallback(
    async (area: Feature<Polygon | MultiPolygon>) => {
      await updateVersion({ ID: versionId, UploadedArea: toMultiPolygonFeature(area) }).unwrap();
    },
    [updateVersion, versionId]
  );

  return { saveBoundary, isSaving: isLoading };
};

/** Return a callback that removes the version's boundary (`version.UploadedArea`). */
export const useClearBoundary = (): (() => Promise<void>) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [updateVersion] = useUpdateVersionMutation();

  return useCallback(async () => {
    await updateVersion({ ID: versionId, UploadedArea: null }).unwrap();
  }, [updateVersion, versionId]);
};
